import { useState, useEffect, useMemo } from "react";
import { apiService } from "../api/api";
import { ChannelManagerOption } from "../views/YouTubeScorecard/Objects/multiSelectObjects";

type FormattedChannelManagers = {
  [name: string]: {
    name: string;
    email: string;
  };
};

export const useChannelManagerData = () => {
  const [channelManagerListData, setChannelManagerListData] =
    useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const channelManagerOptionsForFilter = useMemo(() => {
    if (!channelManagerListData) {
      console.log("[useChannelManagerData] No data available");
      return [];
    }

    console.log("[useChannelManagerData] Processing data:", channelManagerListData);

    // If data is the array format: [{ id:'Name', ChannelManagerName:'Name', ChannelManagerEmail:'Email' }, ...]
    if (Array.isArray(channelManagerListData)) {
      const options = channelManagerListData
        .filter((manager) => manager.ChannelManagerName && manager.ChannelManagerEmail)
        .map((manager) => ({
          label: manager.ChannelManagerName,
          value: manager.ChannelManagerEmail,
        }));
      console.log("[useChannelManagerData] Processed array options:", options.length);
      return options;
    }
    // If data is the object map format: {'Name': { ChannelManagerName:'Name', ChannelManagerEmail:'Email' }, ...}
    else if (
      typeof channelManagerListData === "object" &&
      channelManagerListData !== null
    ) {
      const options = Object.values(channelManagerListData)
        .filter((managerData: any) => managerData.ChannelManagerName && managerData.ChannelManagerEmail)
        .map((managerData: any) => ({
          label: managerData.ChannelManagerName,
          value: managerData.ChannelManagerEmail,
        }));
      console.log("[useChannelManagerData] Processed object options:", options.length);
      return options;
    }
    return [];
  }, [channelManagerListData]);

  const fetchChannelManagerData = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to sync channel manager list from BigQuery to Firestore
      try {
        const syncResult = await apiService.youtubeScorecard.getChannelMangerListFromBQ();
        console.log("[useChannelManagerData] BigQuery sync result:", syncResult);
      } catch (syncError) {
        console.warn("[useChannelManagerData] BigQuery sync failed (non-fatal):", syncError);
        // Continue - we'll still try to fetch from Firestore
      }

      // Then fetch the processed data from Firestore
      const rawChannelManagerData =
        await apiService.youtubeScorecard.updateChannelManagerListInFirestore();

      console.log("[useChannelManagerData] Firestore fetch result:", rawChannelManagerData);
      console.log("[useChannelManagerData] Data type:", typeof rawChannelManagerData);
      console.log("[useChannelManagerData] Is array:", Array.isArray(rawChannelManagerData));
      console.log("[useChannelManagerData] Length:", Array.isArray(rawChannelManagerData) ? rawChannelManagerData.length : 'N/A');

      // Process the data based on its structure
      if (Array.isArray(rawChannelManagerData) && rawChannelManagerData.length > 0) {
        console.log("[useChannelManagerData] First item sample:", rawChannelManagerData[0]);
        setChannelManagerListData(rawChannelManagerData);
      } else if (
        typeof rawChannelManagerData === "object" &&
        rawChannelManagerData !== null &&
        Object.keys(rawChannelManagerData).length > 0
      ) {
        setChannelManagerListData(rawChannelManagerData);
      } else {
        console.warn("[useChannelManagerData] No channel manager data found in Firestore");
        setChannelManagerListData([]);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch channel manager data"
      );
      console.error("[useChannelManagerData] Error fetching channel manager data:", err);
      setChannelManagerListData([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchChannelManagerData();
  };

  useEffect(() => {
    fetchChannelManagerData();
  }, []);

  // Debug log the final options being returned
  console.log("[useChannelManagerData] Returning channelManagerOptionsForFilter:", channelManagerOptionsForFilter);

  return {
    channelManagerListData,
    channelManagerOptionsForFilter,
    loading,
    error,
    refreshData,
  };
};
